// src/components/Experiment/ResultsVisualizationStage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Alert, Spinner, Button, Modal, ProgressBar } from 'react-bootstrap';
import AnalysisResultCard from './AnalysisResultCard';
import ResultsSummaryCard from './ResultsSummaryCard';
import VisualizationDialog from './VisualizationDialog';
import InferenceDialog from './InferenceDialog';
import {
  getAnalysesWithVisualizations,
  getAnalysesStatuses,
  deleteAnalysis,
  batchDeleteAnalyses,
  createVisualization,
  deleteVisualization,
  createAnalysisInference,
  deleteAnalysisInference,
  getAnalysisSummariesStatus,
  waitForSummaryUpdate
} from '../../api/photoProcessingApi';
import './ResultsVisualizationStage.css';

// Configuration for stability
const CONFIG = {
  POLLING_INTERVAL: 8000,        // Reduced for quicker updates
  MAX_RETRIES: 3,
  RETRY_DELAY: 5000,
  BATCH_DELETE_SIZE: 3,
  CACHE_DURATION: 30000,
  MAX_POLLING_DURATION: 900000,  // 15 min – then pause auto-refresh, keep manual Refresh
};

const isPendingStatus = (status) =>
  status === 'pending' || status === 'queued' || status === 'running';

const ResultsVisualizationStage = ({ experiment, onExperimentUpdate }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVisualizationDialog, setShowVisualizationDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showInferenceDialog, setShowInferenceDialog] = useState(false);
  const [inferenceAnalysis, setInferenceAnalysis] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [pollingPaused, setPollingPaused] = useState(false);

  // Refs for polling control
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastPollTimeRef = useRef(0);
  const lastSuccessfulLoadRef = useRef(0);
  const summaryCardRef = useRef(null);
  const analysesRef = useRef([]);
  const inFlightRef = useRef(false);
  const pollStartedAtRef = useRef(0);
  const lastStatusesRef = useRef(null);

  // Keep a ref copy of `analyses` so the poll timer can read fresh data
  // without being re-created on every change.
  useEffect(() => {
    analysesRef.current = analyses;
  }, [analyses]);

  const refreshSummaryWithVersionTracking = useCallback(async (oldVersion) => {
    try {
      const wasUpdated = await waitForSummaryUpdate(experiment.id, oldVersion, 15);
      if (wasUpdated) {
        const status = await getAnalysisSummariesStatus(experiment.id);
        if (status.success) {
          setSummaryVersion(status.version);
        }
        setRefreshKey(prev => prev + 1);
      }
      return wasUpdated;
    } catch (error) {
      console.error('Error waiting for summary update:', error);
      return false;
    }
  }, [experiment.id]);

  // Check if there are any pending analyses, visualizations or predictions (inferences)
  const hasPendingItems = useCallback((analysesList) => {
    if (!analysesList || analysesList.length === 0) return false;
    const hasPendingAnalysis = analysesList.some(a => isPendingStatus(a.status));
    const hasPendingViz = analysesList.some(a =>
      a.visualizations?.some(v => isPendingStatus(v.status))
    );
    const hasPendingInference = analysesList.some(a =>
      a.inferences?.some(i => isPendingStatus(i.status))
    );
    return hasPendingAnalysis || hasPendingViz || hasPendingInference;
  }, []);

  const hasPending = useMemo(() => hasPendingItems(analyses), [analyses, hasPendingItems]);

  // Purely derived: the poll loop is active whenever there is pending work
  // and we are not backing off / paused.
  const isPolling = hasPending && !isRateLimited && !pollingPaused;

  const loadAnalyses = useCallback(async (showLoadingIndicator = true) => {
    const now = Date.now();
    if (now - lastSuccessfulLoadRef.current < 3000 && !showLoadingIndicator) {
      // Throttle rapid calls
      return false;
    }

    try {
      if (showLoadingIndicator) {
        setLoading(true);
      }
      setError(null);

      const analysesWithVisualizations = await getAnalysesWithVisualizations(experiment.id);

      if (!isMountedRef.current) return;

      setAnalyses(analysesWithVisualizations);
      analysesRef.current = analysesWithVisualizations;
      lastSuccessfulLoadRef.current = Date.now();

      // Update summary version
      try {
        const status = await getAnalysisSummariesStatus(experiment.id);
        if (status.success) {
          setSummaryVersion(status.version);
        }
      } catch (error) {
        console.error('Error getting summary version:', error);
      }
      return true;
    } catch (error) {
      console.error('Error loading analyses:', error);
      if (error.response?.status === 429) {
        setError('Too many requests. Please wait before refreshing.');
        setIsRateLimited(true);
        setTimeout(() => {
          if (isMountedRef.current) {
            setIsRateLimited(false);
            setError(null);
          }
        }, 30000);
      } else {
        setError('Failed to load analyses: ' + (error.message || 'Server error'));
      }
      return false;
    } finally {
      if (showLoadingIndicator && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [experiment.id]);

  // Initial load + unmount cleanup.
  useEffect(() => {
    isMountedRef.current = true;
    if (experiment.id) {
      loadAnalyses();
    }
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [experiment.id, loadAnalyses]);

  // Light polling: a snapshot of statuses (no results) is compared with the previous one;
  // the full with-visualizations data is reloaded only when it has changed.
  // null (first poll, failed request or skipped reload) means "reload".
  const statusesChanged = useCallback(async () => {
    try {
      const snapshot = JSON.stringify(await getAnalysesStatuses(experiment.id));
      const changed = snapshot !== lastStatusesRef.current;
      lastStatusesRef.current = snapshot;
      return changed;
    } catch (error) {
      console.error('Error polling analysis statuses:', error);
      lastStatusesRef.current = null;
      return true;
    }
  }, [experiment.id]);

  const calculateAdaptiveInterval = useCallback(() => {
    const list = analysesRef.current || [];
    const runningCount = list.reduce((sum, a) => {
      const aRunning = a.status === 'running' ? 1 : 0;
      const vRunning = a.visualizations?.filter(v => v.status === 'running').length || 0;
      return sum + aRunning + vRunning;
    }, 0);
    if (runningCount > 10) return 15000;
    if (runningCount > 5) return 10000;
    return CONFIG.POLLING_INTERVAL;
  }, []);

  // Declarative poller: a self-scheduling timeout that lives only while there
  // is pending work. No `isPolling` flag is used as control flow, so it can
  // never get stuck in the "Running forever" state.
  useEffect(() => {
    if (!experiment.id || !hasPending || isRateLimited || pollingPaused) {
      return undefined;
    }

    let cancelled = false;
    if (!pollStartedAtRef.current) {
      pollStartedAtRef.current = Date.now();
    }

    async function poll() {
      if (cancelled || !isMountedRef.current) return;

      if (pollStartedAtRef.current &&
          Date.now() - pollStartedAtRef.current > CONFIG.MAX_POLLING_DURATION) {
        setPollingPaused(true);
        return;
      }

      if (inFlightRef.current) {
        scheduleNext(1000);
        return;
      }

      const interval = calculateAdaptiveInterval();
      const sinceLast = Date.now() - lastPollTimeRef.current;
      if (sinceLast < interval) {
        scheduleNext(interval - sinceLast);
        return;
      }

      lastPollTimeRef.current = Date.now();
      inFlightRef.current = true;
      try {
        if (await statusesChanged()) {
          const loaded = await loadAnalyses(false);
          if (!loaded) {
            lastStatusesRef.current = null;  // retry the full reload on the next poll
          }
        }
      } finally {
        inFlightRef.current = false;
      }

      scheduleNext(calculateAdaptiveInterval());
    }

    function scheduleNext(delay) {
      if (cancelled || !isMountedRef.current) return;
      pollingTimeoutRef.current = setTimeout(poll, delay);
    }

    pollingTimeoutRef.current = setTimeout(poll, calculateAdaptiveInterval());

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [experiment.id, hasPending, isRateLimited, pollingPaused, loadAnalyses, statusesChanged, calculateAdaptiveInterval]);

  // On the pending -> idle transition: reset the polling-duration timer and
  // force a one-off summary-card refresh (skip the initial mount).
  const hadPendingRef = useRef(false);
  useEffect(() => {
    if (hadPendingRef.current && !hasPending) {
      pollStartedAtRef.current = 0;
      setRefreshKey(prev => prev + 1);
    }
    hadPendingRef.current = hasPending;
  }, [hasPending]);

  const refreshAllData = async () => {
    // Manual refresh always re-enables auto-polling.
    setPollingPaused(false);
    pollStartedAtRef.current = 0;

    let currentVersion = summaryVersion;
    try {
      const status = await getAnalysisSummariesStatus(experiment.id);
      if (status.success) {
        currentVersion = status.version;
      }
    } catch (error) {
      console.error('Error getting summary version:', error);
    }

    setRefreshKey(prev => prev + 1);
    await loadAnalyses(true);
    await refreshSummaryWithVersionTracking(currentVersion);
  };

  const handleDeleteAnalysis = async (analysisId) => {
    try {
      await deleteAnalysis(analysisId);

      if (!isMountedRef.current) return;

      setAnalyses(prev => prev.filter(analysis => analysis.id !== analysisId));

      let currentVersion = summaryVersion;
      try {
        const status = await getAnalysisSummariesStatus(experiment.id);
        if (status.success) {
          currentVersion = status.version;
        }
      } catch (error) {
        console.error('Error getting summary version:', error);
      }

      await loadAnalyses(false);
      await refreshSummaryWithVersionTracking(currentVersion);
      // The declarative poller stops itself once `hasPending` is false.

    } catch (error) {
      console.error('Error deleting analysis:', error);
      if (error.response?.status === 429) {
        setError('Too many requests. Please wait before deleting more analyses.');
      } else {
        setError('Failed to delete analysis: ' + error.message);
      }
    }
  };

  const handleDeleteAllAnalyses = async () => {
    if (analyses.length === 0) return;

    try {
      setDeletingAll(true);
      setError(null);

      let currentVersion = summaryVersion;
      try {
        const status = await getAnalysisSummariesStatus(experiment.id);
        if (status.success) {
          currentVersion = status.version;
        }
      } catch (error) {
        console.error('Error getting summary version:', error);
      }

      const analysisIds = analyses.map(a => a.id);
      const response = await batchDeleteAnalyses(analysisIds);

      if (response.success) {
        console.log(`Deleted ${response.deleted_count} analyses`);
        if (response.skipped_ids && response.skipped_ids.length > 0) {
          setError(`Warning: ${response.skipped_ids.length} analyses could not be deleted due to permissions.`);
        }

        if (isMountedRef.current) {
          setAnalyses([]);
          setShowDeleteAllModal(false);
          await loadAnalyses(false);
          await refreshSummaryWithVersionTracking(currentVersion);
        }
      } else {
        throw new Error(response.error || 'Batch delete failed');
      }

    } catch (error) {
      console.error('Error deleting all analyses:', error);
      setError('Failed to delete all analyses: ' + error.message);
    } finally {
      if (isMountedRef.current) {
        setDeletingAll(false);
      }
    }
  };

  const handleCreateVisualization = (analysis) => {
    setSelectedAnalysis(analysis);
    setShowVisualizationDialog(true);
  };

  const handleVisualizationSubmit = async (visualizationConfig) => {
    try {
      const visualization = await createVisualization(selectedAnalysis.id, visualizationConfig);

      if (!isMountedRef.current) return;

      setAnalyses(prev => prev.map(analysis =>
        analysis.id === selectedAnalysis.id
          ? {
              ...analysis,
              visualizations: [...(analysis.visualizations || []), visualization]
            }
          : analysis
      ));

      // Adding a pending visualization flips `hasPending` -> the declarative
      // poller effect picks it up automatically. Clear any earlier pause.
      if (isPendingStatus(visualization.status)) {
        setPollingPaused(false);
        pollStartedAtRef.current = 0;
      }

      setShowVisualizationDialog(false);
      setSelectedAnalysis(null);

    } catch (error) {
      console.error('Error creating visualization:', error);
      if (error.response?.status === 429) {
        setError('Too many requests. Please wait before creating more visualizations.');
      } else {
        setError('Failed to create visualization: ' + error.message);
      }
    }
  };

  const handleDeleteVisualization = async (analysisId, visualizationId) => {
    try {
      await deleteVisualization(visualizationId);

      if (!isMountedRef.current) return;

      setAnalyses(prev => prev.map(analysis =>
        analysis.id === analysisId
          ? {
              ...analysis,
              visualizations: analysis.visualizations.filter(viz => viz.id !== visualizationId)
            }
          : analysis
      ));

    } catch (error) {
      console.error('Error deleting visualization:', error);
      if (error.response?.status !== 429) {
        setError('Failed to delete visualization: ' + error.message);
      }
    }
  };

  const handleCreateInference = (analysis) => {
    setInferenceAnalysis(analysis);
    setShowInferenceDialog(true);
  };

  // Errors are thrown back to InferenceDialog, which shows them and stays open
  const handleInferenceSubmit = async (inferenceConfig) => {
    const analysisId = inferenceAnalysis.id;
    const inference = await createAnalysisInference(analysisId, inferenceConfig);

    if (!isMountedRef.current) return;

    setAnalyses(prev => prev.map(analysis =>
      analysis.id === analysisId
        ? {
            ...analysis,
            inferences: [inference, ...(analysis.inferences || [])]
          }
        : analysis
    ));

    // Adding a pending inference flips `hasPending` -> the declarative
    // poller effect picks it up automatically. Clear any earlier pause.
    if (isPendingStatus(inference.status)) {
      setPollingPaused(false);
      pollStartedAtRef.current = 0;
    }

    setShowInferenceDialog(false);
    setInferenceAnalysis(null);
  };

  const handleDeleteInference = async (analysisId, inferenceId) => {
    try {
      await deleteAnalysisInference(inferenceId);

      if (!isMountedRef.current) return;

      setAnalyses(prev => prev.map(analysis =>
        analysis.id === analysisId
          ? {
              ...analysis,
              inferences: (analysis.inferences || []).filter(inf => inf.id !== inferenceId)
            }
          : analysis
      ));

    } catch (error) {
      console.error('Error deleting prediction:', error);
      setError('Failed to delete prediction: ' + error.message);
    }
  };

  // Compute batch progress from analyses list
  const computeBatchProgress = useCallback(() => {
    if (!analyses || analyses.length === 0) return null;

    const status_counts = {
      total: analyses.length,
      pending: 0,
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };

    analyses.forEach(a => {
      switch (a.status) {
        case 'pending': status_counts.pending++; break;
        case 'queued': status_counts.queued++; break;
        case 'running': status_counts.running++; break;
        case 'completed': status_counts.completed++; break;
        case 'failed': status_counts.failed++; break;
        default: break;
      }
    });

    const completed = status_counts.completed + status_counts.failed;
    const progress_percentage = status_counts.total > 0 ? (completed / status_counts.total) * 100 : 0;

    // Also check for pending visualizations to keep progress bar active
    const hasPendingViz = analyses.some(a =>
      a.visualizations?.some(v => v.status === 'pending' || v.status === 'queued' || v.status === 'running')
    );

    const is_processing = (status_counts.pending + status_counts.queued + status_counts.running) > 0 || hasPendingViz;

    return { status_counts, progress_percentage, is_processing };
  }, [analyses]);

  const renderBatchProgress = () => {
    const progress = computeBatchProgress();
    if (!progress || !progress.is_processing) return null;

    const { status_counts, progress_percentage } = progress;

    return (
      <div className="batch-progress-container mb-4 p-3 bg-light rounded">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <strong>Processing Analyses & Visualizations</strong>
            {pendingRefresh && (
              <span className="ms-2 text-info">
                <Spinner animation="border" size="sm" className="me-1" />
                Refreshing summary...
              </span>
            )}
          </div>
          <div className="text-muted">
            <small>
              {status_counts.completed + status_counts.failed} / {status_counts.total} complete
            </small>
          </div>
        </div>

        <ProgressBar
          now={progress_percentage}
          label={`${Math.round(progress_percentage)}%`}
          variant={status_counts.failed > 0 ? "warning" : "primary"}
          animated={status_counts.running > 0}
          className="mb-2"
        />

        <div className="d-flex justify-content-between small text-muted flex-wrap">
          <div>
            <span className="text-success">✓ {status_counts.completed} completed</span>
            {status_counts.running > 0 && (
              <span className="text-info ms-3">
                <Spinner animation="border" size="sm" className="me-1" />
                {status_counts.running} running
              </span>
            )}
            {status_counts.queued > 0 && (
              <span className="text-secondary ms-3">⏳ {status_counts.queued} queued</span>
            )}
            {status_counts.pending > 0 && (
              <span className="text-warning ms-3">⏸ {status_counts.pending} pending</span>
            )}
            {status_counts.failed > 0 && (
              <span className="text-danger ms-3">✗ {status_counts.failed} failed</span>
            )}
          </div>
          {isPolling && !isRateLimited && (
            <div className="mt-1 mt-sm-0">
              <Spinner animation="border" size="sm" className="me-1" />
              <span>updating every {Math.round(CONFIG.POLLING_INTERVAL/1000)}s...</span>
            </div>
          )}
        </div>

        {isRateLimited && (
          <Alert variant="warning" className="mt-2 mb-0 small">
            <i className="bi bi-hourglass-split me-2"></i>
            Rate limit reached. Updates will resume shortly.
          </Alert>
        )}

        {pollingPaused && !isRateLimited && (
          <Alert variant="info" className="mt-2 mb-0 small">
            <i className="bi bi-pause-circle me-2"></i>
            Auto-refresh paused after 15 minutes. Press <strong>Refresh</strong> to check for updates.
          </Alert>
        )}

        {status_counts.failed > 0 && (
          <Alert variant="warning" className="mt-2 mb-0 small">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Some analyses failed. Check individual analysis cards for details.
          </Alert>
        )}
      </div>
    );
  };

  if (loading && analyses.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading analysis results...</p>
      </div>
    );
  }

  return (
    <div className="results-visualization-stage">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4>Analysis Results & Visualizations</h4>
            <p className="text-muted mb-0">
              View and manage your analysis results, create visualizations, and export data.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-danger"
              onClick={() => setShowDeleteAllModal(true)}
              disabled={analyses.length === 0 || deletingAll || loading || isRateLimited}
              className="delete-all-button"
              size="sm"
            >
              {deletingAll ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>
                  Delete All
                </>
              )}
            </Button>
            <Button
              variant="outline-primary"
              onClick={refreshAllData}
              disabled={loading || isRateLimited}
              size="sm"
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {renderBatchProgress()}

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          {isRateLimited && (
            <Button variant="outline-danger" size="sm" onClick={() => {
              setIsRateLimited(false);
              setError(null);
            }}>
              Dismiss
            </Button>
          )}
        </Alert>
      )}

      <ResultsSummaryCard
        key={refreshKey}
        ref={summaryCardRef}
        experimentId={experiment.id}
        analyses={analyses}
        onDeleteCombination={async (deletedAnalysisIds) => {
          setAnalyses(prev => prev.filter(analysis => !deletedAnalysisIds.includes(analysis.id)));

          let currentVersion = summaryVersion;
          try {
            const status = await getAnalysisSummariesStatus(experiment.id);
            if (status.success) {
              currentVersion = status.version;
            }
          } catch (error) {
            console.error('Error getting summary version:', error);
          }

          setRefreshKey(prev => prev + 1);
          await loadAnalyses(false);
          await refreshSummaryWithVersionTracking(currentVersion);
          // The declarative poller stops itself once `hasPending` is false.
        }}
        onAnalysesUpdate={refreshAllData}
      />

      {analyses.length === 0 ? (
        <Alert variant="info" className="text-center py-5">
          <div>
            <i className="bi bi-bar-chart display-1 text-muted mb-3"></i>
            <h5>No Analyses Yet</h5>
            <p className="text-muted mb-0">
              Go back to the Data Analysis stage to run your first analysis.
            </p>
          </div>
        </Alert>
      ) : (
        <Row>
          {analyses.map(analysis => (
            <Col key={analysis.id} xs={12} className="mb-4" id={`analysis-${analysis.id}`}>
              <AnalysisResultCard
                analysis={analysis}
                onDelete={() => handleDeleteAnalysis(analysis.id)}
                onCreateVisualization={() => handleCreateVisualization(analysis)}
                onDeleteVisualization={(vizId) => handleDeleteVisualization(analysis.id, vizId)}
                onCreateInference={() => handleCreateInference(analysis)}
                onDeleteInference={(inferenceId) => handleDeleteInference(analysis.id, inferenceId)}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Delete All Confirmation Modal */}
      <Modal show={showDeleteAllModal} onHide={() => setShowDeleteAllModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Delete All Analyses
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <i className="bi bi-exclamation-octagon-fill me-2"></i>
            <strong>Warning: This action cannot be undone!</strong>
          </Alert>

          <p>
            You are about to delete <strong>{analyses.length}</strong> analysis{analyses.length !== 1 ? 's' : ''} and all associated visualizations.
          </p>

          <div className="analysis-count-breakdown">
            <p className="mb-2"><strong>Breakdown:</strong></p>
            <ul className="mb-0">
              <li>{analyses.length} analysis item{analyses.length !== 1 ? 's' : ''}</li>
              <li>
                {analyses.reduce((total, analysis) => total + (analysis.visualizations?.length || 0), 0)}
                visualization{analyses.reduce((total, analysis) => total + (analysis.visualizations?.length || 0), 0) !== 1 ? 's' : ''}
              </li>
            </ul>
          </div>

          <div className="mt-3">
            <p className="mb-0 text-muted">
              <small>
                <i className="bi bi-info-circle me-1"></i>
                This will permanently remove all analysis results, plots, and configuration data.
              </small>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteAllModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllAnalyses}
            disabled={deletingAll}
          >
            {deletingAll ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash-fill me-2"></i>
                Delete All Analyses
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Visualization Dialog */}
      <VisualizationDialog
        show={showVisualizationDialog}
        onHide={() => {
          setShowVisualizationDialog(false);
          setSelectedAnalysis(null);
        }}
        analysis={selectedAnalysis}
        onSubmit={handleVisualizationSubmit}
      />

      {/* Predict Unknown Samples Dialog */}
      <InferenceDialog
        show={showInferenceDialog}
        onHide={() => {
          setShowInferenceDialog(false);
          setInferenceAnalysis(null);
        }}
        analysis={inferenceAnalysis}
        onSubmit={handleInferenceSubmit}
      />
    </div>
  );
};

export default ResultsVisualizationStage;